import React, { useState } from 'react';
import { Modal, Button } from '@/components/ui';
import { ShieldCheck } from 'lucide-react';
import { LegalStatus } from '@/features/security/api/legal-api';
// #1074 — los enlaces apuntaban a /docs/legal/*, que la SPA sirve con 200 y su
// propio index: el usuario aceptaba sin llegar a leer. Una sola fuente para que
// no aparezca una cuarta variante de la ruta.
import { LEGAL_LINKS } from '@/shared/constants/legal-links';

interface LegalConsentModalProps {
  isOpen: boolean;
  estado: LegalStatus | null;
  enviando: boolean;
  error: string | null;
  onAceptar: () => void;
}

/**
 * #1060 — el pedido de aceptación de términos y privacidad (Ley 172-13).
 *
 * NO OFRECE SALIDA, y eso es la función, no un descuido: `showCloseButton`
 * false, `closeOnOverlayClick` false y `onClose` no-op — las tres vías de
 * cierre que expone el Modal, cerradas a propósito. Un modal esquivable produce
 * exactamente lo que ya teníamos —el usuario adentro y la base sin prueba—
 * sólo que además con la apariencia de que se le preguntó.
 *
 * Las dos casillas van separadas y ambas obligatorias porque son DOS documentos
 * con dos ciclos de vida: el backend las valida por separado (`@Equals(true)` en
 * cada una) y las guarda con su propia fecha y su propia versión. Con una sola
 * casilla "acepto todo", el día que cambie el texto de uno y no el del otro no
 * queda registro de cuál aceptó: el dato para distinguirlos nunca se escribió.
 *
 * La versión se muestra tal como la manda el server: es el dato que convierte
 * "aceptó algo, un martes" en una prueba oponible.
 */
const LegalConsentModal: React.FC<LegalConsentModalProps> = ({
  isOpen,
  estado,
  enviando,
  error,
  onAceptar,
}) => {
  const [terminos, setTerminos] = useState(false);
  const [privacidad, setPrivacidad] = useState(false);
  const listo = terminos && privacidad && !enviando;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      size="md"
      showCloseButton={false}
      closeOnOverlayClick={false}
      title="Antes de continuar"
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-6 w-6 shrink-0 text-primary-600" aria-hidden="true" />
          <p className="text-sm text-gray-600">
            Para seguir usando la plataforma necesitamos tu aceptación de los
            términos y de la política de privacidad. Guardamos la fecha y la
            versión de lo que aceptas, como exige la Ley 172-13.
          </p>
        </div>

        <div className="space-y-3">
          <label htmlFor="legal-terminos" className="flex items-start gap-3 cursor-pointer">
            <input
              id="legal-terminos"
              name="legal-terminos"
              type="checkbox"
              checked={terminos}
              onChange={(e) => setTerminos(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm text-gray-700">
              Acepto los{' '}
              <a
                href={LEGAL_LINKS.terminos}
                target="_blank"
                rel="noreferrer"
                className="text-primary-600 underline"
              >
                términos y condiciones
              </a>
              {estado?.currentTermsVersion && (
                <span className="text-gray-400"> (versión {estado.currentTermsVersion})</span>
              )}
            </span>
          </label>

          <label htmlFor="legal-privacidad" className="flex items-start gap-3 cursor-pointer">
            <input
              id="legal-privacidad"
              name="legal-privacidad"
              type="checkbox"
              checked={privacidad}
              onChange={(e) => setPrivacidad(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm text-gray-700">
              Acepto la{' '}
              <a
                href={LEGAL_LINKS.privacidad}
                target="_blank"
                rel="noreferrer"
                className="text-primary-600 underline"
              >
                política de privacidad
              </a>
              {estado?.currentPrivacyVersion && (
                <span className="text-gray-400"> (versión {estado.currentPrivacyVersion})</span>
              )}
            </span>
          </label>
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="flex justify-end">
          <Button onClick={onAceptar} disabled={!listo}>
            {enviando ? 'Guardando…' : 'Aceptar y continuar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LegalConsentModal;
